/**
 * Enhanced Frontend Authentication Service
 * 
 * Provides comprehensive authentication management with:
 * - Multi-session support with device tracking
 * - Automatic token refresh with rotation
 * - Secure token storage and management
 * - Risk-based authentication handling
 * - Multi-tab synchronization
 * - Comprehensive error handling
 */

import EnhancedSessionManager from '../utils/EnhancedSessionManager';

interface LoginResponse {
  token: string;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  user: {
    id: string;
    email: string;
    displayName: string;
    profilePicture?: string;
    [key: string]: any;
  };
  session: {
    sessionId: string;
    expiresIn: number;
    deviceInfo?: {
      platform?: string;
      browser?: string;
      timezone?: string;
      language?: string;
    };
  };
  isNewUser?: boolean;
  timestamp: string;
}

interface UserValidationResponse {
  success: boolean;
  user: {
    id: string;
    email: string;
    displayName: string;
    profilePicture?: string;
    [key: string]: any;
  };
  session: {
    sessionId: string;
    deviceId: string;
    tokenType: string;
    expiresAt: string;
    issuedAt: string;
    jti: string;
    deviceInfo?: {
      platform?: string;
      browser?: string;
      timezone?: string;
      language?: string;
      lastActivity?: string;
      riskScore?: number;
    };
  };
  security: {
    activeSessions: number;
    maxSessions: number;
    accountLocked: boolean;
    lastActivity: string;
  };
  timestamp: string;
  requestId: string;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  sessionId: string;
  expiresIn: number;
  tokenType: string;
  security: {
    tokenRotated: boolean;
    deviceVerified: boolean;
    riskAssessed: boolean;
  };
  timestamp: string;
  requestId: string;
}

interface LogoutResponse {
  success: boolean;
  message: string;
  action: 'logout_single' | 'logout_all_sessions';
  sessionId?: string;
  userId?: string;
  revokedSessions?: number;
  security?: {
    allDevicesLoggedOut?: boolean;
    securityAction?: string;
    timestamp: string;
  };
  timestamp: string;
  requestId: string;
}

class EnhancedAuthService {
  private static instance: EnhancedAuthService;
  private sessionManager: typeof EnhancedSessionManager;
  
  private constructor() {
    this.sessionManager = EnhancedSessionManager;
    this.setupSessionCallbacks();
  }
  
  public static getInstance(): EnhancedAuthService {
    if (!EnhancedAuthService.instance) {
      EnhancedAuthService.instance = new EnhancedAuthService();
    }
    return EnhancedAuthService.instance;
  }
  
  /**
   * Setup session manager callbacks for enhanced security
   */
  private setupSessionCallbacks(): void {
    this.sessionManager.setEventCallbacks({
      onSessionExpired: () => {
        console.log('🚨 Enhanced session expired, triggering logout');
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
      },
      onTokenRefreshed: (tokens) => {
        console.log('🔄 Enhanced tokens refreshed successfully');
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: tokens }));
      },
      onSecurityViolation: (details) => {
        console.log('🚨 Security violation detected:', details);
        window.dispatchEvent(new CustomEvent('auth:security-violation', { detail: details }));
      },
      onAccountLocked: (details) => {
        console.log('🔒 Account locked:', details);
        window.dispatchEvent(new CustomEvent('auth:account-locked', { detail: details }));
      }
    });
  }
  
  /**
   * Get comprehensive device information
   */
  private getDeviceInfo(): any {
    const userAgent = navigator.userAgent;
    let platform = 'Unknown';
    let browser = 'Unknown';
    
    // Detect platform
    if (userAgent.includes('Windows')) platform = 'Windows';
    else if (userAgent.includes('Mac')) platform = 'macOS';
    else if (userAgent.includes('Linux')) platform = 'Linux';
    else if (userAgent.includes('Android')) platform = 'Android';
    else if (userAgent.includes('iOS')) platform = 'iOS';
    
    // Detect browser
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    
    return {
      userAgent,
      platform,
      browser,
      screen: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      timestamp: new Date().toISOString(),
      // Additional security fingerprinting
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
      cookieEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      hardwareConcurrency: navigator.hardwareConcurrency || 1
    };
  }
  
  /**
   * Login with Google OAuth token using enhanced security
   */
  public async loginWithGoogle(googleToken: string, deviceInfo?: any): Promise<{
    accessToken: string;
    refreshToken?: string;
    user: any;
    session?: {
      sessionId: string;
      expiresIn: number;
      deviceInfo?: any;
    };
    isNewUser?: boolean;
  }> {
    try {
      console.log('🔐 Starting enhanced Google OAuth login...');
      
      const response = await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          idToken: googleToken,
          deviceInfo: deviceInfo || this.getDeviceInfo()
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Login failed: ${response.status}`);
      }
      
      const data: LoginResponse = await response.json();
      
      // Validate response structure
      if (!data.accessToken && !data.token) {
        throw new Error('No access token received from server');
      }
      
      if (!data.user || !data.user.id) {
        throw new Error('Invalid user data received from server');
      }
      
      // Use accessToken if available, fallback to token for backward compatibility
      const accessToken = data.accessToken || data.token;
      const refreshToken = data.refreshToken;
      const sessionId = data.session?.sessionId || `session-${Date.now()}`;
      const expiresIn = data.session?.expiresIn || 15 * 60; // Default 15 minutes
      
      // Create enhanced session
      await this.sessionManager.createSession(
        accessToken,
        refreshToken || accessToken,
        data.user,
        sessionId,
        expiresIn,
        {
          tokenType: data.tokenType || 'Bearer',
          deviceInfo: data.session?.deviceInfo,
          isNewUser: data.isNewUser || false,
          timestamp: data.timestamp
        }
      );
      
      console.log('✅ Enhanced Google OAuth login successful', {
        userId: data.user.id,
        email: data.user.email,
        sessionId,
        isNewUser: data.isNewUser,
        deviceInfo: data.session?.deviceInfo
      });
      
      return {
        accessToken,
        refreshToken,
        user: data.user,
        session: {
          sessionId,
          expiresIn,
          deviceInfo: data.session?.deviceInfo
        },
        isNewUser: data.isNewUser
      };
      
    } catch (error: any) {
      console.error('❌ Enhanced Google OAuth login failed:', error);
      
      // Clear any partial session data
      this.sessionManager.clearSession();
      
      throw error;
    }
  }
  
  /**
   * Validate session with token parameter
   */
  public async validateSession(token?: string): Promise<any> {
    try {
      console.log('🔍 Enhanced validateSession called with token type:', typeof token);
      
      const accessToken = token || await this.sessionManager.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }
      
      // Ensure token is a string and not an object
      if (typeof accessToken !== 'string') {
        console.error('❌ Invalid token type for validation:', typeof accessToken, accessToken);
        throw new Error('Invalid token format for validation - expected string, got ' + typeof accessToken);
      }
      
      // Additional validation - ensure token looks like a JWT
      if (!accessToken.includes('.') || accessToken.split('.').length !== 3) {
        console.error('❌ Token does not appear to be a valid JWT:', accessToken.substring(0, 50) + '...');
        throw new Error('Invalid token format - not a valid JWT');
      }
      
      console.log('✅ Token validation passed - type:', typeof accessToken, 'length:', accessToken.length);
      
      const response = await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/users/me/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Validation failed: ${response.status}`);
      }
      
      const data = await response.json();
      return data.user;
    } catch (error: any) {
      console.error('❌ Enhanced session validation failed:', error);
      throw error;
    }
  }

  /**
   * Validate current session with enhanced backend validation
   */
  public async validateCurrentSession(): Promise<{
    isValid: boolean;
    user?: any;
    sessionInfo?: any;
    securityInfo?: any;
    error?: string;
    errorCode?: string;
  }> {
    try {
      console.log('🔍 Validating enhanced session with backend...');
      
      const accessToken = await this.sessionManager.getAccessToken();
      if (!accessToken) {
        return {
          isValid: false,
          error: 'No access token available'
        };
      }
      
      const response = await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 401) {
          // Token is invalid, clear session
          this.sessionManager.clearSession();
          return {
            isValid: false,
            error: errorData.error?.message || 'Session expired',
            errorCode: errorData.error?.code
          };
        } else if (response.status === 423) {
          // Account locked
          this.sessionManager.clearSession();
          return {
            isValid: false,
            error: errorData.error?.message || 'Account locked',
            errorCode: 'ACCOUNT_LOCKED'
          };
        }
        
        throw new Error(`Validation failed: ${response.status}`);
      }
      
      const data: UserValidationResponse = await response.json();
      
      if (!data.user) {
        throw new Error('Invalid validation response');
      }
      
      // Update user data and session info
      this.sessionManager.updateUser(data.user);
      this.sessionManager.updateSessionInfo({
        session: data.session,
        security: data.security,
        timestamp: data.timestamp
      });
      
      console.log('✅ Enhanced session validation successful', {
        userId: data.user.id,
        email: data.user.email,
        sessionId: data.session.sessionId,
        activeSessions: data.security.activeSessions,
        riskScore: data.session.deviceInfo?.riskScore
      });
      
      return {
        isValid: true,
        user: data.user,
        sessionInfo: data.session,
        securityInfo: data.security
      };
      
    } catch (error: any) {
      console.error('❌ Enhanced session validation failed:', error);
      
      // Clear session on validation failure
      this.sessionManager.clearSession();
      
      return {
        isValid: false,
        error: error.message || 'Validation failed'
      };
    }
  }
  
  /**
   * Logout from current session
   */
  public async logout(sessionId?: string): Promise<void> {
    try {
      console.log('🚪 Enhanced logout starting...');
      
      const accessToken = await this.sessionManager.getAccessToken();
      const sessionInfo = this.sessionManager.getSessionInfo();
      const targetSessionId = sessionId || sessionInfo?.sessionId;
      
      if (accessToken && targetSessionId) {
        try {
          const response = await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              sessionId: targetSessionId
            })
          });
          
          if (response.ok) {
            const data: LogoutResponse = await response.json();
            console.log('✅ Enhanced logout successful:', data);
          } else {
            console.warn('Backend logout call failed, continuing with local logout');
          }
        } catch (error) {
          console.warn('Backend logout call failed:', error);
        }
      }
      
      this.sessionManager.clearSession();
      console.log('✅ Enhanced logout completed');
      
    } catch (error) {
      console.error('❌ Enhanced logout error:', error);
      // Always clear local session even if backend call fails
      this.sessionManager.clearSession();
    }
  }
  
  /**
   * Logout from all sessions (all devices)
   */
  public async logoutAllSessions(): Promise<void> {
    try {
      console.log('🚪 Enhanced logout from all sessions starting...');
      
      const accessToken = await this.sessionManager.getAccessToken();
      
      if (accessToken) {
        try {
          const response = await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/logout-all`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            }
          });
          
          if (response.ok) {
            const data: LogoutResponse = await response.json();
            console.log('✅ Enhanced logout from all sessions successful:', data);
          } else {
            console.warn('Backend logout-all call failed, continuing with local logout');
          }
        } catch (error) {
          console.warn('Backend logout-all call failed:', error);
        }
      }
      
      this.sessionManager.clearSession();
      console.log('✅ Enhanced logout from all sessions completed');
      
    } catch (error) {
      console.error('❌ Enhanced logout all sessions error:', error);
      // Always clear local session even if backend call fails
      this.sessionManager.clearSession();
    }
  }
  
  /**
   * Get current access token with automatic refresh
   */
  public async getAccessToken(): Promise<string | null> {
    try {
      console.log('🔍 EnhancedAuthService: Getting access token from session manager...');
      const token = await this.sessionManager.getAccessToken();
      console.log('🔍 EnhancedAuthService: Session manager returned:', {
        hasToken: !!token,
        tokenType: typeof token,
        tokenLength: token ? token.length : 0
      });
      return token;
    } catch (error) {
      console.error('❌ EnhancedAuthService: Failed to get access token:', error);
      console.error('❌ EnhancedAuthService: Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : typeof error
      });
      return null;
    }
  }
  
  /**
   * Get current user data
   */
  public getCurrentUser(): any | null {
    return this.sessionManager.getUser();
  }
  
  /**
   * Check if user is authenticated
   */
  public async isAuthenticated(): Promise<boolean> {
    try {
      return await this.sessionManager.isLoggedIn();
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Update current user data
   */
  public updateUser(userData: any): void {
    this.sessionManager.updateUser(userData);
  }
  
  /**
   * Get comprehensive session information
   */
  public getSessionInfo(): any {
    return this.sessionManager.getSessionInfo();
  }
  
  /**
   * Get security information
   */
  public getSecurityInfo(): any {
    return this.sessionManager.getSecurityInfo();
  }
  
  /**
   * Make an authenticated API request with enhanced error handling
   */
  public async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const accessToken = await this.getAccessToken();
    
    if (!accessToken) {
      throw new Error('No authentication token available');
    }
    
    // Add authorization header
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
    };
    
    // Make the request
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    // Handle enhanced error responses
    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({}));
      
      if (errorData.error?.code === 'TOKEN_EXPIRED') {
        console.log('🔄 Token expired, automatic refresh should handle this');
      } else if (errorData.error?.code === 'SESSION_INACTIVE') {
        console.log('🚨 Session inactive, clearing local session');
        this.sessionManager.clearSession();
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
      } else if (errorData.error?.code === 'TOKEN_REVOKED') {
        console.log('🚨 Token revoked, clearing local session');
        this.sessionManager.clearSession();
        window.dispatchEvent(new CustomEvent('auth:security-violation', { 
          detail: { reason: 'Token revoked' } 
        }));
      }
    } else if (response.status === 423) {
      const errorData = await response.json().catch(() => ({}));
      console.log('🔒 Account locked');
      this.sessionManager.clearSession();
      window.dispatchEvent(new CustomEvent('auth:account-locked', { 
        detail: errorData 
      }));
    }
    
    return response;
  }
  
  // Legacy methods for backward compatibility
  
  /**
   * @deprecated Use getAccessToken() instead
   */
  public async getToken(): Promise<string | null> {
    return this.getAccessToken();
  }
  
  /**
   * @deprecated Use getAccessToken() instead
   */
  public getTokenSync(): string | null {
    const user = this.getCurrentUser();
    return user ? 'sync-not-supported' : null;
  }
  
  /**
   * @deprecated Sessions are managed automatically
   */
  public setToken(token: string, expiresIn?: number): void {
    console.warn('setToken is deprecated - sessions are managed automatically');
  }
  
  /**
   * @deprecated Use logout() instead
   */
  public clearToken(): void {
    this.logout();
  }
  
  /**
   * @deprecated Use logout() instead
   */
  public clearTokens(): void {
    this.logout();
  }
}

// Export singleton instance
export const enhancedAuthService = EnhancedAuthService.getInstance();
export default enhancedAuthService;